import React, { useState, useRef, useEffect } from "react";
import { Tag, X, Upload } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { compressImage } from "lib/utils";

const FeaturedWorkModal = ({ open, onClose, onAdd }) => {
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState([]);
  const [addTagsOpen, setAddTagsOpen] = useState(false);
  const [tagInput, setTagInput] = useState("");

  // Multi-file support logic
  const [imagePreviews, setImagePreviews] = useState([]);
  const [rawFiles, setRawFiles] = useState([]);
  const [isCompressing, setIsCompressing] = useState(false);

  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setTitle("");
      setTags([]);
      setTagInput("");
      setImagePreviews([]);
      setRawFiles([]);
      setAddTagsOpen(false);
    }
  }, [open]);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    try {
      setIsCompressing(true);

      const compressedFiles = await Promise.all(
        files.map(async (file) => {
          if (file.type.startsWith('image/')) {
            return await compressImage(file);
          }
          return file;
        })
      );

      const previews = compressedFiles.map((file) => URL.createObjectURL(file));

      setRawFiles((prev) => [...prev, ...compressedFiles]);
      setImagePreviews((prev) => [...prev, ...previews]);
    } catch (error) {
      console.error("Error during file processing:", error);
    } finally {
      setIsCompressing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeTag = (t) => {
    setTags(tags.filter((x) => x !== t));
  };

  const handleAdd = () => {
    if (!title.trim() || rawFiles.length === 0) return;
    onAdd({
      id: Date.now(),
      title,
      tags,
      previews: imagePreviews,
      files: rawFiles,
    });
    onClose();
  };

  return (
    <>
      {/* BACKDROP */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
        onClick={onClose}
      />

      {/* MAIN CENTERED MODAL */}
      <div
        className={`mx-4 lg:mx-0 fixed inset-0 z-50 flex items-center justify-center transition-all duration-300
          ${open ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"}
        `}
      >
        <div className="bg-white md:w-[520px] lg:w-[738px] max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden relative">
          {/* HEADER */}
          <div className="flex items-center justify-between px-6 pt-5">
            <h3 className="text-base font-semibold">Featured Work</h3>
            <button onClick={onClose} className="p-2">
              <X />
            </button>
          </div>

          {/* CONTENT */}
          <div className="px-6 py-4 overflow-auto flex-1">
            <Input
              placeholder="Enter Work Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="h-11 rounded-md mb-4"
            />

            {/* UPLOAD */}
            <div className="space-y-2 relative">

              {imagePreviews.length === 0 ? (
                <div
                  className={`border border-dashed rounded-xl h-48 flex items-center justify-center bg-[#FCF9F6] mb-4 ${isCompressing ? "opacity-50 cursor-wait" : "hover:bg-white/10 hover:border-[#E8D1AB]/40 cursor-pointer"
                    }`}
                  onClick={() => !isCompressing && fileRef.current?.click()}
                >
                  <div className="text-center px-6">
                    <div className="mb-4">
                      <Upload className={`text-2xl mb-2 mx-auto transition-transform ${isCompressing ? "animate-bounce" : ""}`} />
                    </div>
                    <div className="font-semibold text-lg">
                      {isCompressing ? "Optimizing..." : "Upload New Image, Video, Or Browse"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Choose a file (4:3 / 5:4 / 9:16 / 16:9)
                    </div>
                  </div>

                </div>
              ) : (
                // Preview
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 relative">
                  {isCompressing && (
                    <div className="absolute inset-0 z-20 bg-black/40 backdrop-blur-[2px] rounded-[12px] flex items-center justify-center">
                      <div className="w-8 h-8 border-4 border-[#E8D1AB] border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}

                  {imagePreviews.map((src, index) => (
                    <div key={index} className="relative rounded-[12px] overflow-hidden border border-white/20 aspect-square group">
                      <img src={src} className="w-full h-full object-cover" />
                      <button
                        onClick={() => {
                          URL.revokeObjectURL(src);
                          setImagePreviews(prev => prev.filter((_, i) => i !== index));
                          setRawFiles(prev => prev.filter((_, i) => i !== index));
                        }}
                        className="absolute top-2 right-2 bg-black/60 rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                      >
                        <X size={14} className="text-white" />
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="flex items-center justify-center border border-dashed border-white/20 rounded-[12px] aspect-square hover:bg-white/5 transition"
                  >
                    <Upload className="text-white/60" />
                  </button>
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                accept="image/*,video/*"
              />
            </div>

            {/* TAGS SECTION */}
            <div className="mt-6">
              {/* TAG CHIPS */}
              <div className="flex flex-wrap gap-2">
                {tags.map((t) => (
                  <span
                    key={t}
                    className="px-4 py-1 bg-white border rounded-full shadow-sm flex items-center gap-2 text-sm text-black"
                  >
                    {t}
                    <button onClick={() => removeTag(t)}>
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="px-6 py-4 flex justify-between">
            <div className="flex justify-between items-center mb-2">
              <button
                onClick={() => setAddTagsOpen(true)}
                className="flex items-center gap-2 rounded-full"
              >
                <Tag className="w-5 h-5" /> # {tags.length > 0 ? "Edit" : "Add"}{" "}
                Tags
              </button>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="rounded-full px-6"
              >
                Close
              </Button>
              <Button onClick={handleAdd} className="rounded-full px-6">
                Add
              </Button>
            </div>
          </div>

          {/* INNER “ADD TAGS” MODAL */}
          {addTagsOpen && (
            <>
              {/* BACKDROP */}
              <div className="absolute inset-0 bg-black/40 rounded-2xl"></div>

              {/* MODAL */}
              <div
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
                    bg-white w-[85%] rounded-2xl shadow-xl p-6 z-50"
              >
                <h2 className="text-lg font-semibold mb-3">Add Tags</h2>

                {/* INPUT */}
                <Input
                  placeholder="Include relevant tags for your work."
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const trimmed = tagInput.trim();
                      if (trimmed && !tags.includes(trimmed)) {
                        setTags([...tags, trimmed]);
                      }
                      setTagInput("");
                    }
                  }}
                  className="h-11 rounded-xl"
                />

                <p className="text-sm text-muted-foreground mt-2">
                  Nature photography, editing...
                </p>

                {/* TAG PREVIEW */}
                <div className="flex flex-wrap gap-3 mt-4 max-h-40 overflow-auto">
                  {tags.map((t) => (
                    <div
                      key={t}
                      className="px-4 py-1 bg-white border rounded-full shadow-sm flex items-center gap-2 text-sm text-black"
                    >
                      {t}
                      <button
                        onClick={() => setTags(tags.filter((tag) => tag !== t))}
                      >
                        <X size={14} className="text-black" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* ACTION BUTTONS */}
                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setAddTagsOpen(false)}
                    className="rounded-full px-6 bg-[#EFE6D6]"
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      const trimmed = tagInput.trim();
                      if (trimmed && !tags.includes(trimmed)) {
                        setTags([...tags, trimmed]);
                      }
                      setTagInput("");
                    }}
                    className="rounded-full px-6 bg-black text-white"
                  >
                    Add
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div >
    </>
  );
};

export default FeaturedWorkModal;
